import React, { type ComponentProps, useCallback } from 'react';
import { useResponsive, useToggleState } from '@sg/garnish';

import { ReorderConflictModal } from '@order/components';
import {
  OrderFavoriteLineItem,
  type OrderLineItem,
} from '@order/features/orders';
import {
  useNavigateToReorderProductDetails,
  useReorderOrderLineItem,
} from '@order/hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const ReorderOrderFavoriteLineItem = (
  props: ReorderOrderFavoriteLineItemProps,
) => {
  const {
    favoriteLineItem,
    mostRecentOrder,
    isTogglingFavoriteState,
    toggleFavoriteLineItem,
  } = props;

  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  // NOTE: Reorder can be presented in a small modal on large breakpoints and a
  // fullscreen one on the smallest breakpoint which introduces some issues
  // We use different methods of navigation per breakpoint to avoid unexpected
  // issues.
  const shouldReplaceScreenOnNavigation = match([false, true]);

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: isConflictReviewModalVisible,
    toggleOn: showConflictReviewModal,
    toggleOff: hideConflictReviewModal,
  } = useToggleState(false);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToProductDetailsScreen = useNavigateToReorderProductDetails({
    shouldReplaceScreen: shouldReplaceScreenOnNavigation,
  });

  const {
    reorderLineItem,
    isFetching: isReordering,
    unavailableIngredients,
    targetLocation,
  } = useReorderOrderLineItem({
    variation: 'favorite',
    mostRecentOrder,
    productName: favoriteLineItem.product.name,
    onUnavailableIngredientConflict: showConflictReviewModal,
    onSuccess: hideConflictReviewModal,
  });

  const handleNavigateToProductDetailsScreen =
    useCallback<OnProductDetailsLinkPress>(
      (navigationParams) => {
        hideConflictReviewModal();
        navigateToProductDetailsScreen(navigationParams);
      },
      [hideConflictReviewModal, navigateToProductDetailsScreen],
    );

  // ─── Derived Data ────────────────────────────────────────────────────

  const reorderLineItemForce = useCallback(async () => {
    await reorderLineItem(favoriteLineItem.id, true);
  }, [favoriteLineItem.id, reorderLineItem]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <OrderFavoriteLineItem
        favoriteLineItem={favoriteLineItem}
        isReordering={isReordering}
        isTogglingFavoriteState={isTogglingFavoriteState}
        onAddToBagButtonPress={reorderLineItem}
        onToggleFavoriteButtonPress={toggleFavoriteLineItem}
      />

      <ReorderConflictModal
        visible={isConflictReviewModalVisible}
        lineItem={favoriteLineItem}
        isReordering={isReordering}
        onRequestClose={hideConflictReviewModal}
        navigateToProductDetails={handleNavigateToProductDetailsScreen}
        addToBagAnyway={reorderLineItemForce}
        targetLocation={targetLocation}
        unavailableIngredients={unavailableIngredients}
      />
    </>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ReorderOrderFavoriteLineItemProps = {
  favoriteLineItem: FavoriteLineItem;
  mostRecentOrder: UseReorderOrderLineItemParams['mostRecentOrder'];
  isTogglingFavoriteState: boolean;
  toggleFavoriteLineItem: ToggleFavorite;
};

type UseReorderOrderLineItemParams = Parameters<
  typeof useReorderOrderLineItem
>[0];

type FavoriteLineItem = ComponentProps<
  typeof OrderFavoriteLineItem
>['favoriteLineItem'];

type ToggleFavorite = ComponentProps<
  typeof OrderFavoriteLineItem
>['onToggleFavoriteButtonPress'];

type OnProductDetailsLinkPress = ComponentProps<
  typeof OrderLineItem
>['onProductDetailsLinkPress'];
